import Pagination from './pagination';
import React, { useCallback, useEffect, useState } from "react";
import {
  useTable,
  useGlobalFilter,
  useExpanded,
  useSortBy,
  usePagination,
} from "react-table";

function Table({
  columns,
  data,
  filters,
  title,
  initialFilter = null,
  initialValue = null,
  expandedTable = true,
  hasPagination = true,
  hasSearch = true,
  pageChangeHandler = null,
  totalData = null,
  currentPage = null,
}) {
  const [filter, setFilter] = useState("");
  const globalFilterCallback = useCallback(
    (rows, ids, query) => {
      return rows.filter((row) => {
        if (
          initialFilter != null &&
          row.values[initialFilter] !== initialValue &&
          initialValue.toLowerCase() !== "all"
        ) {
          return false;
        }

        for (const filter of filters) {
          if (row.values[filter] !== undefined && row.values[filter] !== null) {
            if (Array.isArray(row.values[filter])) {
              for (var i = 0; i < row.values[filter].length; i++) {
                var value = row.values[filter][i];
                for (var z = 0; z < Object.keys(value).length; z++) {
                  var k = Object.keys(value)[z];

                  if (value[k] !== undefined && value[k] !== null) {
                    if (
                      value[k]
                        .toString()
                        .toLowerCase()
                        .includes(query.toLowerCase())
                    ) {
                      return true;
                    }
                  }
                }
              }
            } else if (typeof row.values[filter] === "object") {
              for (var y = 0; y < Object.keys(row.values[filter]).length; y++) {
                var key = Object.keys(row.values[filter])[y];

                if (
                  row.values[filter][key] !== undefined &&
                  row.values[filter][key] !== null
                ) {
                  if (
                    row.values[filter][key]
                      .toString()
                      .toLowerCase()
                      .includes(query.toLowerCase())
                  ) {
                    return true;
                  }
                }
              }
            } else {
              if (
                row.values[filter]
                  .toString()
                  .toLowerCase()
                  .includes(query.toLowerCase())
              )
                return true;
            }
          }
        }
      });
    },
    [filters]
  );

  const {
    page,
    state,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      globalFilter: globalFilterCallback,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(filter);
    if (!hasPagination) setPageSize(data.length);
  }, [filter, setGlobalFilter]);

  useEffect(() => {
    setGlobalFilter(filter);
  }, [data]);

  return (
    <>
      {/* Header with Title and Search */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-700">{title}</h2>
        {hasSearch && (
          <div className="sm:flex sm:gap-x-2">
            <FilterBar count={rows.length} setFilter={setFilter} />
          </div>
        )}
      </div>
      {/* Table */}
      <div className="flex flex-col mt-4 ">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow-lg rounded-lg overflow-hidden">
              <table
                {...getTableProps()}
                border="1"
                className="min-w-full bg-white divide-y divide-gray-200"
              >
                <thead className="bg-[#2A3042]">
                  {headerGroups.map((headerGroup) => {
                    const { key, ...restHeaderGroupProps } =
                      headerGroup.getHeaderGroupProps();
                    return (
                      <tr
                        key={key}
                        {...restHeaderGroupProps}
                        className="group text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
                      >
                        {headerGroup.headers.map((column) => {
                          const { key, ...restColumn } = column.getHeaderProps(
                            column.getSortByToggleProps(),
                            {
                              style: { width: "10px" },
                            }
                          );
                          return (
                            <th key={key} {...restColumn}>
                              <div className="px-2 py-3 text-center text-xs font-medium uppercase tracking-wider">
                                {column.render("Header")}
                                <span>
                                  {column.isSorted
                                    ? column.isSortedDesc
                                      ? " ▼"
                                      : " ▲"
                                    : ""}
                                </span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="bg-white divide-y divide-gray-200"
                >
                  {page.map((row) => {
                    prepareRow(row);
                    const { key, ...restRowProps } = row.getRowProps();
                    return (
                      <React.Fragment key={key}>
                        <tr {...restRowProps} className="px-2 py-4 hover:bg-gray-100 text-center">
                          {row.cells.map((cell) => {
                            const { key, ...restCellProps } = cell.getCellProps();
                            return (
                              <td key={key} {...restCellProps} className="px-2 py-2">
                                <div className="text-sm text-gray-900">
                                  {cell.render("Cell")}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        {row.isExpanded && expandedTable && (
                          <tr>
                            <td
                              className="px-6 py-4 whitespace-nowrap"
                              colSpan={columns.length}
                            >
                              {expandedTable}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Pagination */}
      {hasPagination && (
        <Pagination
          state={state}
          pageOptions={pageOptions}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          setPageSize={setPageSize}
          pageChangeHandler={pageChangeHandler}
          totalData={totalData}
          currentPage={currentPage}
        />
      )}
    </>
  );
}

function FilterBar({ count, setFilter }) {
  return (
    <label className="flex gap-x-2 items-baseline">
      <span className="text-gray-700">Search: </span>
      <input
        type="text"
        className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        onChange={(e) => {
          setFilter(e.target.value);
        }}
        placeholder={`${count} records...`}
        style={{ marginBottom: "-0.5rem" }}
      />
    </label>
  );
}

export default Table;